import React, { useState, useEffect } from 'react';
import { getQuestions, saveRecord, getPostTestQuestions, getInstructorMap, getActiveInductionByName } from '../services/storageService';
import { generateInductionPDF } from '../services/pdfService';
import { Question, InductionRecord, PostTestQuestion, InstructorMap } from '../types';
import { AREAS, INSTRUCTORS, INITIAL_QUESTIONS, DEFAULT_POST_TEST_QUESTIONS } from '../constants';
import { v4 as uuidv4 } from 'uuid';
import { CheckCircle, AlertTriangle, FileText, Download, ShieldCheck, Loader2, Award, ClipboardCheck, XCircle } from 'lucide-react';
import { SafetyInductionSlides } from './SafetyInductionSlides';

interface InductionFormProps {
  lang?: 'id' | 'en';
}

const t = {
  id: {
    title: "Form Evaluasi Safety Induction",
    subtitle: "PT United Tractors - Marketing Division",
    personalDataTitle: "Data Diri Peserta",
    visitorTypeLabel: "Status Pengunjung",
    visitorTypePlaceholder: "-- Pilih Status Pengunjung --",
    visitorTypeTamu: "Kunjungan Tamu",
    visitorTypeVendor: "Mekanik / Vendor / Pekerja dengan resiko tinggi",
    fullNameLabel: "Nama Lengkap",
    fullNamePlaceholder: "Sesuai KTP/ID",
    jobTitleLabel: "Jabatan",
    jobTitlePlaceholder: "Manager, Staff, Driver, dll.",
    companyLabel: "Nama Perusahaan / Instansi",
    companyPlaceholder: "PT...",
    inductionDateLabel: "Tanggal Induksi",
    areaLabel: "Area Kerja",
    areaPlaceholder: "-- Pilih Area Kerja --",
    instructorLabel: "Pemberi Materi (ESR Officer)",
    instructorPlaceholder: "-- Pilih Nama Pemateri --",
    activityLabel: "Keperluan / Aktivitas",
    activityPlaceholder: "Contoh: Meeting Project, Kunjungan Tamu, Vendor Maintenance",
    evalTitle: "Evaluasi Pemahaman",
    yes: "Ya",
    no: "Tidak",
    btnNext: "Selanjutnya / Next",
    btnNextSub: "Lanjutkan ke konfirmasi",
    alertMissingFields: "Mohon lengkapi data diri, status pengunjung, jabatan, nama perusahaan, dan jawab semua pertanyaan. / Please complete your personal data, visitor status, job title, company name, and answer all questions.",
    alertPostTestIncomplete: "Harap jawab semua pertanyaan post-test sebelum submit. / Please answer all post-test questions before submitting.",
    alertPostTestFail: "Gagal memproses data. Silakan coba kembali.",
    modalCommitTitle: "Konfirmasi Komitmen",
    modalCommitSub: "Commitment Confirmation",
    modalCommitQuote: "Saya siap berpartisipasi sesuai dengan safety induction dan saya telah paham dengan peraturan dan rambu-rambu keselamatan yang ada di area Marketing Divisi. Saya setuju untuk mematuhi peraturan keselamatan ini selama saya berkunjung.",
    modalBtnNo: "Tidak",
    modalBtnYes: "SIAP / READY",
    modalBtnYesSub: "Mulai Post-Test",
    modalBtnYesGuestSub: "Kirim Evaluasi",
    postTestTitle: "Post-Test Safety Induksi",
    postTestSub: "Sebagai Syarat Pelaksanaan Mandatory Key Action Astra di Area Marketing.",
    postTestRulesTitle: "Aturan Penilaian / Grading Requirements:",
    postTestRule1: "Total nilai kelulusan minimal: 70 poin.",
    postTestRule2: "Setiap pertanyaan memiliki bobot poin tersendiri berdasarkan tingkat risikonya.",
    postTestRule3: "Sertifikat keselamatan akan diunduh secara otomatis setelah Anda mencapai nilai kelulusan.",
    postTestBtnSubmit: "Submit Jawaban",
    postTestBtnSubmitting: "Memproses...",
    passedTitle: "Selamat! Anda Lulus! / Congratulations! Passed!",
    passedSub: "Skor Post-Test Anda / Your Post-Test Score",
    passedBadge: "LULUS / PASSED",
    passedText: "Formulir safety induction dan kuis evaluasi pasca-tes Anda telah disubmit dengan sukses. Sertifikat digital Anda sedang diunduh secara otomatis. Harap simpan sertifikat tersebut sebagai bukti resmi keselamatan kerja.",
    btnDownloadAgain: "Unduh Sertifikat Lagi",
    btnFinished: "Selesai / Restart",
    failedTitle: "Maaf, Anda Belum Lulus / Assessment Not Passed",
    failedSub: "Skor Post-Test Anda / Your Post-Test Score",
    failedBadge: "BELUM LULUS (Syarat Kelulusan ≥ 70) / FAILED",
    failedText: "Sesuai standar operasional PT United Tractors, peserta wajib mencapai skor minimal 70 untuk lulus induksi keselamatan kerja. Silakan ulangi tes ini setelah mempelajari kembali ketentuan safety di lokasi.",
    btnRetryTest: "Ulangi Post-Test / Retake Test",
    btnStartOver: "Mulai Dari Awal / Start Over"
  },
  en: {
    title: "Safety Induction Evaluation Form",
    subtitle: "PT United Tractors - Marketing Division",
    personalDataTitle: "Participant Personal Data",
    visitorTypeLabel: "Visitor Status",
    visitorTypePlaceholder: "-- Select Visitor Status --",
    visitorTypeTamu: "Guest Visit",
    visitorTypeVendor: "Mechanic / Vendor / High-Risk Worker",
    fullNameLabel: "Full Name",
    fullNamePlaceholder: "As per ID Card / Passport",
    jobTitleLabel: "Job Title",
    jobTitlePlaceholder: "Manager, Staff, Driver, etc.",
    companyLabel: "Company / Vendor Name",
    companyPlaceholder: "PT...",
    inductionDateLabel: "Induction Date",
    areaLabel: "Work Area",
    areaPlaceholder: "-- Select Work Area --",
    instructorLabel: "Instructor (ESR Officer)",
    instructorPlaceholder: "-- Select Instructor --",
    activityLabel: "Purpose / Activity",
    activityPlaceholder: "e.g. Project Meeting, Visitor, Vendor Maintenance",
    evalTitle: "Comprehension Evaluation",
    yes: "Yes",
    no: "No",
    btnNext: "Next / Selanjutnya",
    btnNextSub: "Proceed to confirmation",
    alertMissingFields: "Please complete your personal data, visitor status, job title, company name, and answer all questions.",
    alertPostTestIncomplete: "Please answer all post-test questions before submitting.",
    alertPostTestFail: "Failed to process data. Please try again.",
    modalCommitTitle: "Commitment Confirmation",
    modalCommitSub: "Verification",
    modalCommitQuote: "I am ready to participate according to the safety induction and I have understood the safety regulations and signs in the Marketing Division area. I agree to comply with these safety regulations during my visit.",
    modalBtnNo: "No",
    modalBtnYes: "READY / YES",
    modalBtnYesSub: "Start Post-Test",
    modalBtnYesGuestSub: "Submit Evaluation",
    postTestTitle: "Safety Induction Post-Test",
    postTestSub: "Required for the implementation of Astra's Mandatory Key Action at Marketing Area.",
    postTestRulesTitle: "Grading Requirements:",
    postTestRule1: "Minimum passing score: 70 points.",
    postTestRule2: "Each question has its own weight based on its risk level.",
    postTestRule3: "Your digital safety certificate will automatically download once you pass the assessment.",
    postTestBtnSubmit: "Submit Answers",
    postTestBtnSubmitting: "Processing...",
    passedTitle: "Congratulations! You Passed!",
    passedSub: "Your Post-Test Score",
    passedBadge: "PASSED / LULUS",
    passedText: "Your safety induction form and post-test assessment have been completed successfully. Your digital certificate is downloading automatically. Please retain it for verification purposes.",
    btnDownloadAgain: "Download Certificate Again",
    btnFinished: "Finish / Restart",
    failedTitle: "Assessment Not Passed / Try Again",
    failedSub: "Your Post-Test Score",
    failedBadge: "FAILED (Passing score ≥ 70)",
    failedText: "In accordance with United Tractors standards, participants must achieve a minimum score of 70 to pass the safety induction. Please retake the test after reviewing safety requirements.",
    btnRetryTest: "Retake Post-Test",
    btnStartOver: "Start Over"
  }
};

export const InductionForm: React.FC<InductionFormProps> = ({ lang = 'id' }) => {
  const [questions, setQuestions] = useState<Question[]>(INITIAL_QUESTIONS);
  const [postTestQuestions, setPostTestQuestions] = useState<PostTestQuestion[]>(DEFAULT_POST_TEST_QUESTIONS);
  const [dynamicAreas, setDynamicAreas] = useState<string[]>(AREAS);
  const [dynamicInstructorsMap, setDynamicInstructorsMap] = useState<InstructorMap>(INSTRUCTORS);
  const [formData, setFormData] = useState(() => {
    const savedDraft = localStorage.getItem('ut_induction_draft_v2');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.formData) return parsed.formData;
      } catch (e) {
        console.error("Error reading initial formData draft:", e);
      }
    }
    return {
      fullName: '',
      jobTitle: '',
      company: '',
      inductionDate: new Date().toISOString().split('T')[0],
      area: '',
      activity: '',
      instructor: '',
      visitorType: '' as 'TAMU' | 'VENDOR' | '',
    };
  });
  const [savedRecord, setSavedRecord] = useState<InductionRecord | null>(null);
  const [activeDuplicateRecord, setActiveDuplicateRecord] = useState<InductionRecord | null>(null);
  const [forceFillNewForm, setForceFillNewForm] = useState(() => {
    const savedDraft = localStorage.getItem('ut_induction_draft_v2');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.forceFillNewForm !== undefined) return parsed.forceFillNewForm;
      } catch (e) {}
    }
    return false;
  });
  const [answers, setAnswers] = useState<Record<string, boolean>>(() => {
    const savedDraft = localStorage.getItem('ut_induction_draft_v2');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.answers) return parsed.answers;
      } catch (e) {}
    }
    return {};
  });
  const [postTestAnswers, setPostTestAnswers] = useState<Record<string, number>>(() => {
    const savedDraft = localStorage.getItem('ut_induction_draft_v2');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.postTestAnswers) return parsed.postTestAnswers;
      } catch (e) {}
    }
    return {};
  });
  const [currentFormStep, setCurrentFormStep] = useState<'eval' | 'posttest'>(() => {
    const savedDraft = localStorage.getItem('ut_induction_draft_v2');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.currentFormStep) return parsed.currentFormStep;
      } catch (e) {}
    }
    return 'eval';
  });
  const [testResult, setTestResult] = useState<{ score: number; passed: boolean } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  const handleClearDraft = () => {
    localStorage.removeItem('ut_induction_draft_v2');
    setFormData({
      fullName: '',
      jobTitle: '',
      company: '',
      inductionDate: new Date().toISOString().split('T')[0],
      area: '',
      activity: '',
      instructor: '',
      visitorType: '' as 'TAMU' | 'VENDOR' | '',
    });
    setAnswers({});
    setPostTestAnswers({});
    setCurrentFormStep('eval');
    setForceFillNewForm(false);
    setDraftRestored(false);
  };

  useEffect(() => {
    const savedDraft = localStorage.getItem('ut_induction_draft_v2');
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (parsed.formData && (parsed.formData.fullName || parsed.formData.visitorType || parsed.formData.company || Object.keys(parsed.answers || {}).length > 0)) {
          setDraftRestored(true);
        }
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (testResult && testResult.passed) {
      localStorage.removeItem('ut_induction_draft_v2');
      return;
    }

    const hasData = formData.fullName || formData.visitorType || formData.company || Object.keys(answers).length > 0 || Object.keys(postTestAnswers).length > 0;
    if (!hasData) {
      return;
    }

    const draftData = {
      formData,
      answers,
      postTestAnswers,
      currentFormStep,
      forceFillNewForm
    };
    localStorage.setItem('ut_induction_draft_v2', JSON.stringify(draftData));
  }, [formData, answers, postTestAnswers, currentFormStep, forceFillNewForm, testResult]);

  const getExpiryStatus = (inductionDateStr: string) => {
    if (!inductionDateStr) return { status: 'expired' as const, expiryDateStr: '' };
    const inductionDate = new Date(inductionDateStr);
    const expiryDate = new Date(inductionDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 1); // 1 Year validity
    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const expiryMidnight = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
    const msDiff = expiryMidnight.getTime() - todayMidnight.getTime();
    const daysLeft = Math.ceil(msDiff / (1000 * 60 * 60 * 24));
    
    let status: 'valid' | 'expiring_soon' | 'expired' = 'valid';
    if (daysLeft < 0) {
      status = 'expired';
    } else if (daysLeft <= 30) {
      status = 'expiring_soon';
    }
    
    return {
      daysLeft,
      status,
      expiryDateStr: expiryDate.toISOString().split('T')[0]
    };
  };

  const findActiveRecord = (name: string, currentRecords: InductionRecord[]) => {
    if (!name || !name.trim()) return null;
    const normalizedInput = name.trim().toLowerCase();
    
    return currentRecords.find(r => {
      if (r.fullName && r.fullName.trim().toLowerCase() === normalizedInput) {
        const expiry = getExpiryStatus(r.inductionDate);
        const hasPassed = r.postTestPassed !== false; // passed post-test or guest
        return expiry.status !== 'expired' && hasPassed;
      }
      return false;
    });
  };

  useEffect(() => {
    let isMounted = true;
    getQuestions().then(q => {
      if (isMounted) setQuestions(q);
    });

    getPostTestQuestions().then(pt => {
      if (isMounted) setPostTestQuestions(pt);
    });

    getInstructorMap().then(instructorMap => {
      if (isMounted && instructorMap) {
        setDynamicInstructorsMap(instructorMap);
        setDynamicAreas(Object.keys(instructorMap));
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({
      ...formData,
      area: e.target.value,
      instructor: '' // Reset instructor when area changes
    });
  };

  const handleAnswer = (qId: string, val: boolean) => {
    setAnswers(prev => ({ ...prev, [qId]: val }));
  };

  const validateForm = () => {
    if (!formData.visitorType || !formData.fullName || !formData.jobTitle || !formData.company || !formData.area || !formData.activity || !formData.instructor) return false;
    // Check if all questions are answered
    const allAnswered = questions.every(q => answers[q.id] !== undefined);
    return allAnswered;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      const activeRecord = await getActiveInductionByName(formData.fullName);
      if (activeRecord && !forceFillNewForm) {
        setActiveDuplicateRecord(activeRecord);
        return;
      }
      setShowModal(true);
    } else {
      alert(t[lang].alertMissingFields);
    }
  };

  const handleConfirm = async (ready: boolean) => {
    if (!ready) {
      setShowModal(false);
      return;
    }

    setShowModal(false);

    if (formData.visitorType === 'TAMU') {
      setIsSaving(true);
      try {
        const record: InductionRecord = {
          id: uuidv4(),
          ...formData,
          answers,
          postTestPassed: true, // Guests are passed automatically as they don't take post-tests
          timestamp: Date.now()
        };

        await saveRecord(record);
        setSavedRecord(record);
        setTestResult({ score: 100, passed: true });

        await generateInductionPDF(record);
      } catch (e) {
        console.error(e);
        alert(t[lang].alertPostTestFail);
      } finally {
        setIsSaving(false);
      }
    } else {
      setCurrentFormStep('posttest');
      // Scroll to top of page when entering post-test
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePostTestAnswerSelect = (qId: string, optionIdx: number) => {
    setPostTestAnswers(prev => ({ ...prev, [qId]: optionIdx }));
  };

  const validatePostTest = () => {
    return postTestQuestions.every(q => postTestAnswers[q.id] !== undefined);
  };

  const handlePostTestSubmit = async () => {
    if (!validatePostTest()) {
      alert(t[lang].alertPostTestIncomplete);
      return;
    }

    setIsSaving(true);
    try {
      const totalPointsPossible = postTestQuestions.reduce((sum, q) => sum + q.points, 0);
      const pointsScored = postTestQuestions.reduce((sum, q) => {
        const answer = postTestAnswers[q.id];
        return sum + (answer === q.correctOptionIndex ? q.points : 0);
      }, 0);

      const finalScore = totalPointsPossible > 0 ? Math.round((pointsScored / totalPointsPossible) * 100) : 0;
      const hasPassed = finalScore >= 70;

      const record: InductionRecord = {
        id: uuidv4(),
        ...formData,
        answers,
        postTestAnswers,
        postTestScore: finalScore,
        postTestPassed: hasPassed,
        timestamp: Date.now()
      };

      await saveRecord(record);
      setSavedRecord(record);
      setTestResult({ score: finalScore, passed: hasPassed });

      if (hasPassed) {
        await generateInductionPDF(record);
      }
    } catch (e) {
      console.error(e);
      alert(t[lang].alertPostTestFail);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRetryPostTest = () => {
    setPostTestAnswers({});
    setTestResult(null);
    setCurrentFormStep('posttest');
  };

  if (testResult) {
    if (testResult.passed) {
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-white shadow-2xl rounded-xl max-w-2xl mx-auto mt-10 border border-green-200">
          <Award className="w-24 h-24 text-green-500 mb-4 animate-bounce" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
            {formData.visitorType === 'TAMU' 
              ? (lang === 'id' ? "Pendaftaran Evaluasi Berhasil!" : "Evaluation Registration Successful!") 
              : t[lang].passedTitle}
          </h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 w-full text-center my-4">
            {formData.visitorType === 'TAMU' ? (
              <>
                <span className="text-gray-500 uppercase tracking-widest text-xs font-semibold">
                  {lang === 'id' ? "STATUS EVALUASI" : "EVALUATION STATUS"}
                </span>
                <p className="text-3xl font-extrabold text-green-600 mt-1">SELESAI / COMPLETED</p>
                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full uppercase mt-3">
                  {lang === 'id' ? "KUNJUNGAN TAMU / GUEST" : "GUEST VISIT"}
                </span>
              </>
            ) : (
              <>
                <span className="text-gray-500 uppercase tracking-widest text-xs font-semibold">{t[lang].passedSub}</span>
                <p className="text-5xl font-extrabold text-green-600 mt-1">{testResult.score} / 100</p>
                <span className="inline-block bg-green-200 text-green-800 text-xs font-bold px-3 py-1 rounded-full uppercase mt-3">{t[lang].passedBadge}</span>
              </>
            )}
          </div>
          <p className="text-center text-gray-600 mb-6 text-sm leading-relaxed">
            {formData.visitorType === 'TAMU' 
              ? (lang === 'id' 
                  ? "Formulir safety induction Anda telah disubmit dengan sukses. Sebagai tamu, Anda telah melewati persyaratan kuis pasca-tes. Sertifikat digital Anda sedang diunduh secara otomatis. Harap simpan sertifikat tersebut sebagai bukti resmi keselamatan kerja."
                  : "Your safety induction form has been submitted successfully. As a guest, you have bypassed the post-test quiz. Your digital certificate is downloading automatically. Please retain it for safety proof.")
              : t[lang].passedText}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={async () => {
                if (savedRecord) {
                  await generateInductionPDF(savedRecord);
                }
              }}
              className="flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 font-medium transition cursor-pointer"
            >
              <Download size={16} /> {t[lang].btnDownloadAgain}
            </button>
            <button 
              onClick={() => {
                localStorage.removeItem('ut_induction_draft_v2');
                window.location.reload();
              }}
              className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition cursor-pointer"
            >
              {t[lang].btnFinished}
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-white shadow-2xl rounded-xl max-w-2xl mx-auto mt-10 border border-red-200">
          <XCircle className="w-24 h-24 text-red-500 mb-4 animate-pulse" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">{t[lang].failedTitle}</h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 w-full text-center my-4">
            <span className="text-gray-500 uppercase tracking-widest text-xs font-semibold">{t[lang].failedSub}</span>
            <p className="text-5xl font-extrabold text-red-600 mt-1">{testResult.score} / 100</p>
            <span className="inline-block bg-red-200 text-red-800 text-xs font-bold px-3 py-1 rounded-full uppercase mt-3">{t[lang].failedBadge}</span>
          </div>
          <p className="text-center text-gray-600 mb-6 text-sm leading-relaxed">
            {t[lang].failedText}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={handleRetryPostTest}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 font-semibold transition cursor-pointer"
            >
              {t[lang].btnRetryTest}
            </button>
            <button 
              onClick={() => {
                localStorage.removeItem('ut_induction_draft_v2');
                window.location.reload();
              }}
              className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition cursor-pointer"
            >
              {t[lang].btnStartOver}
            </button>
          </div>
        </div>
      );
    }
  }

  if (currentFormStep === 'posttest') {
    const completedCount = postTestQuestions.filter(q => postTestAnswers[q.id] !== undefined).length;
    const totalCount = postTestQuestions.length;
    const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

    return (
      <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-2xl overflow-hidden mb-12 animate-fade-in-up transition-all">
        {/* Post-Test Header */}
        <div className="bg-blue-600 p-4 sm:p-6 flex items-center gap-4 text-white">
          <div className="bg-white p-2 rounded-full shadow-sm text-blue-600 shrink-0">
            <ClipboardCheck className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{t[lang].postTestTitle}</h1>
            <p className="text-blue-100 text-xs sm:text-sm font-medium mt-0.5">{t[lang].postTestSub}</p>
          </div>
        </div>

        {/* Live Quiz Progress Bar */}
        <div className="bg-slate-55 border-b border-slate-100 px-4 sm:px-8 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 select-none">
            <span className="flex h-2.5 w-2.5 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-600"></span>
            </span>
            <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">
              {lang === 'id' ? "Progres Kuis" : "Quiz Progress"}
            </span>
          </div>

          <div className="flex items-center gap-3 w-full sm:max-w-xs">
            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden shadow-inner">
              <div 
                className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out" 
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
            <span className="text-xs font-mono font-extrabold text-slate-600 whitespace-nowrap bg-slate-100 px-2 py-1 rounded">
              {completedCount} / {totalCount} ({Math.round(progressPercent)}%)
            </span>
          </div>
        </div>

        <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
          {/* Draft Restored Notification Banner for Post Test */}
          {draftRestored && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in shadow-xs">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-lg text-blue-600 shrink-0">
                  <ClipboardCheck className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-blue-900">
                    {lang === 'id' ? "Melanjutkan Draft Sebelumnya" : "Resuming Previous Draft"}
                  </h4>
                  <p className="text-xs text-blue-700 mt-0.5">
                    {lang === 'id' 
                      ? "Kami memulihkan pengisian formulir & kuis Anda secara otomatis." 
                      : "Your safety induction & quiz progress has been automatically restored."}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                <button
                  type="button"
                  onClick={() => setDraftRestored(false)}
                  className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition shadow-sm cursor-pointer"
                >
                  {lang === 'id' ? "Lanjutkan" : "Continue"}
                </button>
                <button
                  type="button"
                  onClick={handleClearDraft}
                  className="px-3.5 py-1.5 border border-blue-200 bg-white hover:bg-blue-50 text-blue-700 text-xs font-bold rounded-lg transition cursor-pointer"
                >
                  {lang === 'id' ? "Mulai Baru" : "Start Fresh"}
                </button>
              </div>
            </div>
          )}

          <div className="bg-blue-50/50 border border-blue-150 p-3.5 sm:p-4 rounded-xl flex items-start gap-3">
            <Award className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-blue-900 leading-relaxed">
              <p className="font-bold">{t[lang].postTestRulesTitle}</p>
              <ul className="list-disc list-inside mt-1.5 space-y-1 text-blue-800 font-medium">
                <li>{t[lang].postTestRule1}</li>
                <li>{t[lang].postTestRule2}</li>
                <li>{t[lang].postTestRule3}</li>
              </ul>
            </div>
          </div>

          <SafetyInductionSlides lang={lang} />

          <div className="space-y-6">
            {postTestQuestions.map((q, idx) => {
              const selectedIdx = postTestAnswers[q.id];
              return (
                <div key={q.id} className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md hover:border-slate-300 transition-all duration-200">
                  <h4 className="text-slate-900 font-bold text-sm sm:text-base md:text-lg leading-snug flex flex-wrap items-center gap-2">
                    <span>{idx + 1}. {lang === 'en' ? (q.textEn || q.text) : q.text}</span>
                    <span className="inline-block text-[10px] font-extrabold bg-blue-50 text-blue-600 px-2.5 py-0.5 rounded-md font-mono border border-blue-100 shrink-0 uppercase select-none">
                      {q.points} pt
                    </span>
                  </h4>

                  {lang === 'id' && q.textEn && (
                    <p className="text-slate-400 italic text-xs mt-1.5 font-medium">{q.textEn}</p>
                  )}
                  {lang === 'en' && q.textEn && q.textEn !== q.text && (
                    <p className="text-slate-400 italic text-xs mt-1.5 font-medium">{q.text}</p>
                  )}

                  {/* Render Question Image if present */}
                  {q.imageUrl && (
                    <div className="mt-3.5 max-w-full rounded-xl overflow-hidden border border-slate-150 bg-slate-50 p-1.5 inline-block">
                      <img src={q.imageUrl} alt={`Visual helper for question ${idx + 1}`} className="max-h-52 sm:max-h-60 rounded-lg object-contain w-full" />
                    </div>
                  )}
 
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3.5 mt-4 sm:mt-5">
                    {q.options.map((opt, optIdx) => {
                      const isSelected = selectedIdx === optIdx;
                      return (
                        <label 
                          key={optIdx}
                          onClick={() => handlePostTestAnswerSelect(q.id, optIdx)}
                          className={`p-3.5 rounded-xl border transition-all duration-200 flex items-start gap-3 select-none active:scale-[0.99] cursor-pointer ${
                            isSelected 
                              ? 'bg-blue-50/70 border-blue-600 text-blue-950 font-bold ring-2 ring-blue-500/20 shadow-xs' 
                              : 'bg-white border-slate-200/90 hover:border-slate-300 text-slate-700 hover:bg-slate-50 shadow-xs'
                          }`}
                        >
                          <input 
                            type="radio"
                            name={`ptq-${q.id}`}
                            checked={isSelected}
                            readOnly
                            className="w-4 h-4 mt-0.5 text-blue-600 focus:ring-blue-500 shrink-0 cursor-pointer"
                          />
                          <div className="flex flex-col gap-1 w-full min-w-0">
                            <span className="text-xs sm:text-sm leading-relaxed">{String.fromCharCode(65 + optIdx)}. {lang === 'en' ? (q.optionsEn?.[optIdx] || opt) : opt}</span>
                            {lang === 'id' && q.optionsEn?.[optIdx] && (
                              <span className="text-[11px] text-slate-400 italic font-medium leading-normal">{q.optionsEn[optIdx]}</span>
                            )}
                            {lang === 'en' && q.optionsEn?.[optIdx] && q.optionsEn[optIdx] !== opt && (
                              <span className="text-[11px] text-slate-400 italic font-medium leading-normal">{opt}</span>
                            )}
                            
                            {/* Render Option Image if present */}
                            {q.optionImages?.[optIdx] && (
                              <div className="mt-2 w-full max-w-[150px] border border-slate-150 bg-white rounded-lg p-1 overflow-hidden shadow-xs">
                                <img src={q.optionImages[optIdx]} alt={`Visual option ${String.fromCharCode(65 + optIdx)}`} className="max-h-24 max-w-full mx-auto object-contain rounded-md" />
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-4 flex justify-end">
            <button 
              onClick={handlePostTestSubmit}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-base w-full sm:w-auto disabled:opacity-50 cursor-pointer"
            >
              {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
              <div className="text-left font-medium">
                <span>{isSaving ? t[lang].postTestBtnSubmitting : t[lang].postTestBtnSubmit}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-xl overflow-hidden mb-12">
      {/* Header */}
      <div className="bg-yellow-400 p-6 flex items-center gap-4">
        <div className="bg-white p-2 rounded-full shadow-sm shadow-yellow-200">
          <FileText className="w-8 h-8 text-yellow-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t[lang].title}</h1>
          <p className="text-yellow-905 text-sm font-semibold text-amber-950">{t[lang].subtitle}</p>
        </div>
      </div>

      <div className="p-8 space-y-8">
        {/* Draft Restored Notification Banner */}
        {draftRestored && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in shadow-xs">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded-lg text-blue-600 shrink-0">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-blue-900">
                  {lang === 'id' ? "Melanjutkan Draft Sebelumnya" : "Resuming Previous Draft"}
                </h4>
                <p className="text-xs text-blue-700 mt-0.5">
                  {lang === 'id' 
                    ? "Kami memulihkan pengisian formulir Anda secara otomatis." 
                    : "Your safety induction progress has been automatically restored."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
              <button
                type="button"
                onClick={() => setDraftRestored(false)}
                className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition shadow-sm cursor-pointer"
              >
                {lang === 'id' ? "Lanjutkan" : "Continue"}
              </button>
              <button
                type="button"
                onClick={handleClearDraft}
                className="px-3.5 py-1.5 border border-blue-200 bg-white hover:bg-blue-50 text-blue-700 text-xs font-bold rounded-lg transition cursor-pointer"
              >
                {lang === 'id' ? "Mulai Baru" : "Start Fresh"}
              </button>
            </div>
          </div>
        )}
        
        {/* Section 1: Personal Data */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
            <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
            <div>
              {t[lang].personalDataTitle}
            </div>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t[lang].visitorTypeLabel} <span className="text-red-500">*</span>
              </label>
              <select 
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none bg-white font-semibold text-gray-700"
                value={formData.visitorType}
                onChange={e => setFormData({...formData, visitorType: e.target.value as 'TAMU' | 'VENDOR'})}
              >
                <option value="">{t[lang].visitorTypePlaceholder}</option>
                <option value="TAMU">{t[lang].visitorTypeTamu}</option>
                <option value="VENDOR">{t[lang].visitorTypeVendor}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t[lang].fullNameLabel}
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none font-semibold text-gray-800"
                value={formData.fullName}
                onChange={e => {
                  const val = e.target.value;
                  setFormData({...formData, fullName: val});
                  if (activeDuplicateRecord && val.trim().toLowerCase() !== activeDuplicateRecord.fullName.trim().toLowerCase()) {
                    setActiveDuplicateRecord(null);
                  }
                }}
                onBlur={async () => {
                  if (formData.fullName.trim()) {
                    const activeRecord = await getActiveInductionByName(formData.fullName);
                    if (activeRecord && !forceFillNewForm) {
                      setActiveDuplicateRecord(activeRecord);
                    }
                  }
                }}
                placeholder={t[lang].fullNamePlaceholder}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t[lang].jobTitleLabel}
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                value={formData.jobTitle}
                onChange={e => setFormData({...formData, jobTitle: e.target.value})}
                placeholder={t[lang].jobTitlePlaceholder}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t[lang].companyLabel}
              </label>
              <input 
                type="text" 
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                value={formData.company}
                onChange={e => setFormData({...formData, company: e.target.value})}
                placeholder={t[lang].companyPlaceholder}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t[lang].inductionDateLabel}
              </label>
              <input 
                type="date" 
                className="w-full border border-gray-200 rounded-lg p-2.5 bg-gray-100 text-gray-500 cursor-not-allowed outline-none font-semibold"
                value={formData.inductionDate}
                disabled
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t[lang].areaLabel}
              </label>
              <select 
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                value={formData.area}
                onChange={handleAreaChange}
              >
                <option value="">{t[lang].areaPlaceholder}</option>
                {dynamicAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t[lang].instructorLabel}
              </label>
              <select 
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none bg-white disabled:bg-gray-100 disabled:text-gray-400"
                value={formData.instructor}
                onChange={e => setFormData({...formData, instructor: e.target.value})}
                disabled={!formData.area}
              >
                <option value="">{t[lang].instructorPlaceholder}</option>
                {formData.area && dynamicInstructorsMap[formData.area]?.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t[lang].activityLabel}
              </label>
              <textarea 
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                rows={2}
                value={formData.activity}
                onChange={e => setFormData({...formData, activity: e.target.value})}
                placeholder={lang === 'en' ? "e.g. Project Meeting, Visitor, Vendor Maintenance" : "Contoh: Meeting Project, Kunjungan Tamu, Vendor Maintenance"}
              />
            </div>
          </div>
        </section>

        {/* Section 2: Questions */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
            <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
            <div>
              {t[lang].evalTitle}
            </div>
          </h3>

          <div className="space-y-4">
            {questions.map((q, index) => (
              <div key={q.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in">
                <p className="text-gray-805 font-bold">
                  {index + 1}. {lang === 'en' ? (q.textEn || q.text) : q.text}
                </p>
                {lang === 'id' && q.textEn && (
                  <p className="text-gray-500 italic text-xs mt-1.5 pl-4">{q.textEn}</p>
                )}
                {lang === 'en' && q.textEn && q.textEn !== q.text && (
                  <p className="text-gray-400 italic text-xs mt-1.5 pl-4">{q.text}</p>
                )}
                <div className="mt-4 flex gap-4">
                  <label className={`flex items-center gap-2 px-5 py-2 rounded-lg cursor-pointer transition ${answers[q.id] === true ? 'bg-green-100 border-green-500 border text-green-750 font-bold' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}>
                    <input 
                      type="radio" 
                      name={`q-${q.id}`} 
                      className="w-4 h-4 text-green-600 focus:ring-green-500 cursor-pointer"
                      onChange={() => handleAnswer(q.id, true)}
                      checked={answers[q.id] === true}
                    />
                    <span className="text-sm">{t[lang].yes}</span>
                  </label>
                  <label className={`flex items-center gap-2 px-5 py-2 rounded-lg cursor-pointer transition ${answers[q.id] === false ? 'bg-red-100 border-red-500 border text-red-750 font-bold' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}>
                    <input 
                      type="radio" 
                      name={`q-${q.id}`} 
                      className="w-4 h-4 text-red-600 focus:ring-red-500 cursor-pointer"
                      onChange={() => handleAnswer(q.id, false)}
                      checked={answers[q.id] === false}
                    />
                    <span className="text-sm">{t[lang].no}</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="pt-4 flex justify-end">
          <button 
            type="button"
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition transform hover:-translate-y-0.5 flex flex-col items-center cursor-pointer"
          >
            <span>{t[lang].btnNext}</span>
            <span className="text-xs font-normal opacity-90">{t[lang].btnNextSub}</span>
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 animate-fade-in-up border border-gray-100">
            <div className="flex items-center gap-3 text-blue-600 mb-4">
              <AlertTriangle className="w-8 h-8 shrink-0 animate-pulse text-amber-500" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t[lang].modalCommitTitle}</h3>
                <p className="text-xs text-gray-400 italic font-mono">{t[lang].modalCommitSub}</p>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4.5 rounded-lg mb-6 border border-gray-200">
              <p className="text-gray-700 leading-relaxed text-sm font-semibold">
                "{t[lang].modalCommitQuote}"
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button 
                type="button"
                onClick={() => handleConfirm(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold cursor-pointer"
              >
                {t[lang].modalBtnNo}
              </button>
              <button 
                type="button"
                onClick={() => handleConfirm(true)}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2 cursor-pointer shadow-md"
              >
                <CheckCircle className="w-4 h-4" />
                <div className="text-left font-semibold">
                  <span>{t[lang].modalBtnYes}</span>
                  <span className="block text-[10px] font-normal opacity-90">
                    {formData.visitorType === 'TAMU' ? t[lang].modalBtnYesGuestSub : t[lang].modalBtnYesSub}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Duplicate Record Warning Modal */}
      {activeDuplicateRecord && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 border border-green-200 overflow-hidden transform transition-all scale-100">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4 border border-green-200 shadow-sm">
                <ShieldCheck className="w-10 h-10 text-green-600 animate-pulse" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {lang === 'id' ? "Sertifikat Induksi Masih Aktif!" : "Induction Certificate Still Active!"}
              </h3>
              <p className="text-[11px] text-gray-400 italic font-mono mt-0.5 uppercase tracking-wider">
                Active Record Detected
              </p>
              
              <div className="bg-green-50/50 p-4.5 rounded-xl my-5 border border-green-100 w-full text-left space-y-2.5">
                <div className="text-sm">
                  <span className="font-semibold block text-[10px] text-gray-400 uppercase tracking-wider">Nama Lengkap / Full Name</span>
                  <span className="font-bold text-gray-950 text-base">{activeDuplicateRecord.fullName}</span>
                </div>
                <div className="text-sm">
                  <span className="font-semibold block text-[10px] text-gray-400 uppercase tracking-wider">Berlaku S.D / Valid Until</span>
                  <span className="font-bold text-green-700 text-base flex items-center gap-1.5">
                    <span>📅 {getExpiryStatus(activeDuplicateRecord.inductionDate).expiryDateStr}</span>
                    <span className="text-[10px] bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-bold">AKTIF</span>
                  </span>
                </div>
                <div className="text-sm">
                  <span className="font-semibold block text-[10px] text-gray-400 uppercase tracking-wider">Pemberi Materi / Instructor</span>
                  <span className="font-semibold text-gray-700">{activeDuplicateRecord.instructor}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                {lang === 'id' 
                  ? "Anda sudah terdaftar dan memiliki sertifikat keselamatan yang masih berlaku. Anda tidak perlu mengisi kembali formulir ini karena data Anda belum kedaluwarsa."
                  : "You are already registered and have a valid safety certificate. You do not need to fill out this form again because your data has not expired."}
              </p>

              <div className="flex flex-col gap-3 w-full">
                <button
                  type="button"
                  onClick={async () => {
                    await generateInductionPDF(activeDuplicateRecord);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-xl shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Download className="w-5 h-5" />
                  <span>{lang === 'id' ? "Unduh Sertifikat Aktif" : "Download Active Certificate"}</span>
                </button>

                <div className="flex gap-2 w-full mt-1">
                  <button
                    type="button"
                    onClick={() => setActiveDuplicateRecord(null)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-semibold transition cursor-pointer"
                  >
                    {lang === 'id' ? "Tutup" : "Close"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setForceFillNewForm(true);
                      setActiveDuplicateRecord(null);
                    }}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-xs font-semibold transition cursor-pointer"
                  >
                    {lang === 'id' ? "Tetap Isi Formulir Baru" : "Fill New Form"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};